import { useEffect, useRef, useState } from 'react'
import { Search, ShieldAlert, ShieldCheck } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { normalizeRepeatedLabel } from '../lib/displayText'

interface OrganizationMatch {
  id: string
  name: string
  city: string | null
  state: string | null
  org_type: string | null
  certification_status: string | null
}

function formatCertificationStatus(value: string | null | undefined) {
  const labels: Record<string, string> = {
    certified: 'Certified',
    not_certified: 'Not Certified',
    pending: 'Pending Certification',
    denied: 'Certification Denied',
    withdrawn: 'Certification Withdrawn',
  }

  if (!value) return 'Certification Status Unknown'
  return labels[value.toLowerCase()] || value
}

function isInstitutionCertified(value: string | null | undefined) {
  return (value || '').toLowerCase() === 'certified'
}

function namesMatch(query: string, organization: OrganizationMatch | null) {
  return organization != null && query.trim().toLowerCase() === organization.name.trim().toLowerCase()
}

export default function OrganizationCertificationLookup() {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<OrganizationMatch[]>([])
  const [selectedOrganization, setSelectedOrganization] = useState<OrganizationMatch | null>(null)
  const [loading, setLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [fetchError, setFetchError] = useState(false)
  const latestSearchIdRef = useRef(0)
  const lookupRef = useRef<HTMLDivElement | null>(null)
  const manuallyDismissedRef = useRef(false)

  useEffect(() => {
    const searchTerm = query.trim()

    if (searchTerm.length < 2) {
      latestSearchIdRef.current += 1
      manuallyDismissedRef.current = false
      setResults([])
      setLoading(false)
      setHasSearched(false)
      setDropdownOpen(false)
      setFetchError(false)
      return
    }

    const timeout = window.setTimeout(async () => {
      const searchId = latestSearchIdRef.current + 1
      latestSearchIdRef.current = searchId
      setLoading(true)
      setHasSearched(true)
      if (!manuallyDismissedRef.current) {
        setDropdownOpen(true)
      }
      setFetchError(false)

      const stallTimer = window.setTimeout(() => {
        if (latestSearchIdRef.current !== searchId) {
          return
        }

        setResults([])
        setLoading(false)
        setFetchError(true)
      }, 8000)

      try {
        const escaped = searchTerm.replace(/%/g, '\\%').replace(/_/g, '\\_').replace(/[.,()]/g, '')
        const { data, error } = await supabase
          .from('organizations')
          .select('id, name, city, state, org_type, certification_status')
          .eq('is_active', true)
          .or(`name.ilike.%${escaped}%,state.ilike.%${escaped}%`)
          .order('name')
          .limit(3)

        if (latestSearchIdRef.current !== searchId) {
          return
        }

        if (error) {
          console.error('Institution certification lookup failed:', error)
          setResults([])
          setFetchError(true)
        } else {
          setFetchError(false)
          const normalizedResults = ((data || []) as OrganizationMatch[]).map((org) => ({
            ...org,
            name: normalizeRepeatedLabel(org.name),
          }))
          setResults(normalizedResults)
        }
      } catch (error) {
        if (latestSearchIdRef.current !== searchId) {
          return
        }

        console.error('Institution certification lookup timed out or failed:', error)
        setResults([])
        setFetchError(true)
      } finally {
        window.clearTimeout(stallTimer)
        if (latestSearchIdRef.current === searchId) {
          setLoading(false)
        }
      }
    }, 250)

    return () => window.clearTimeout(timeout)
  }, [query])

  useEffect(() => {
    if (!namesMatch(query, selectedOrganization)) {
      setSelectedOrganization(null)
    }
  }, [query, selectedOrganization])

  const showNoResults = hasSearched && !loading && !fetchError && results.length === 0
  const hideSuggestionsForSelection = namesMatch(query, selectedOrganization)
  const showDropdown = dropdownOpen
    && query.trim().length >= 2
    && (loading || hasSearched)
    && !hideSuggestionsForSelection

  useEffect(() => {
    if (!showDropdown) return

    const handlePointerDown = (event: MouseEvent) => {
      if (!lookupRef.current?.contains(event.target as Node)) {
        manuallyDismissedRef.current = true
        setDropdownOpen(false)
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        manuallyDismissedRef.current = true
        setDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [showDropdown])

  const handleSelect = (organization: OrganizationMatch) => {
    setSelectedOrganization(organization)
    setQuery(organization.name)
    setDropdownOpen(false)
    manuallyDismissedRef.current = false
  }

  const handleQueryChange = (nextValue: string) => {
    manuallyDismissedRef.current = false
    setQuery(nextValue)
    if (!namesMatch(nextValue, selectedOrganization)) {
      setSelectedOrganization(null)
    }
  }

  const certified = isInstitutionCertified(selectedOrganization?.certification_status)

  return (
    <section className="relative z-20 overflow-visible bg-neutral-50 py-16 sm:py-20" aria-labelledby="institution-lookup-heading">
      <div className="absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-neutral-100" />
        <img
          src="/media/images/home/us-map-subtle.svg"
          alt=""
          className="absolute inset-0 h-full w-full object-contain object-center opacity-[0.18]"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(246,246,246,0.94)_0%,rgba(246,246,246,0.82)_18%,rgba(246,246,246,0.82)_82%,rgba(246,246,246,0.94)_100%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(0,82,136,0.05),transparent_28%),radial-gradient(circle_at_bottom_left,rgba(94,151,50,0.05),transparent_24%)]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4">
        <div className="mx-auto max-w-[68rem] overflow-visible rounded-[2rem] border border-neutral-200 bg-white/96 p-6 shadow-sm backdrop-blur sm:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:gap-6">
            <div ref={lookupRef} className="relative sm:min-w-0 sm:flex-1">
              <div className="max-w-4xl">
                <p className="text-sm font-semibold uppercase tracking-[0.24em] text-secondary">
                  Institution Search
                </p>
                <h2
                  id="institution-lookup-heading"
                  className="mt-4 text-3xl font-bold tracking-tight text-primary sm:text-4xl"
                >
                  Lookup Certification Status
                </h2>
              </div>

              <div className="relative mt-5">
                <div className="pointer-events-none absolute inset-y-0 left-4 flex items-center">
                  <Search
                    size={18}
                    className="text-neutral-400"
                    aria-hidden="true"
                  />
                </div>
                <input
                  type="search"
                  value={query}
                  onChange={(event) => handleQueryChange(event.target.value)}
                  onFocus={() => {
                    if (query.trim().length >= 2 && (hasSearched || loading)) {
                      manuallyDismissedRef.current = false
                      setDropdownOpen(true)
                    }
                  }}
                  placeholder="Search institution name"
                  role="combobox"
                  aria-label="Search institution certification status"
                  aria-expanded={showDropdown}
                  aria-controls="institution-search-listbox"
                  aria-autocomplete="list"
                  className="h-12 w-full rounded-xl border border-neutral-300 bg-white pl-12 pr-4 text-sm text-neutral-900 outline-none transition-colors placeholder:text-neutral-400 focus:border-secondary focus:ring-2 focus:ring-secondary-lighter"
                />
              </div>

              {showDropdown && (
                <div
                  id="institution-search-dropdown"
                  className="absolute left-0 right-0 top-full z-[60] mt-0.5 overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-xl ring-1 ring-black/5"
                >
                  {loading && (
                    <div className="px-4 py-4 text-sm text-neutral-500">
                      Searching institutions...
                    </div>
                  )}

                  {!loading && fetchError && (
                    <div className="px-4 py-4 text-sm leading-6 text-amber-800">
                      Search failed. Please try again later.
                    </div>
                  )}

                  {!loading && !fetchError && results.length === 0 && (
                    <div className="px-4 py-4 text-sm leading-6 text-neutral-500">
                      No matching institutions found.
                    </div>
                  )}

                  <ul
                    id="institution-search-listbox"
                    role="listbox"
                    className={!loading && results.length > 0 ? 'max-h-60 overflow-y-auto py-2' : 'sr-only'}
                  >
                    {!loading && results.length > 0 && results.map((organization) => {
                        const isSelected = selectedOrganization?.id === organization.id

                        return (
                          <li key={organization.id} className="px-2">
                            <button
                              type="button"
                              role="option"
                              aria-selected={isSelected}
                              onClick={() => handleSelect(organization)}
                              className={`flex w-full items-center rounded-xl px-3 py-3 text-left text-sm font-medium transition-colors ${
                                isSelected
                                  ? 'bg-secondary-lightest text-primary'
                                  : 'text-neutral-800 hover:bg-neutral-50'
                              }`}
                            >
                              <span className="truncate">{organization.name}</span>
                            </button>
                          </li>
                        )
                      })}
                  </ul>
                </div>
              )}
            </div>

            <aside className="rounded-[1.25rem] border border-neutral-200 bg-neutral-50/90 p-4 shadow-sm sm:w-80 sm:flex-none">
              {selectedOrganization ? (
                <div className="space-y-2.5">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-500">
                    Certification Status
                  </p>
                  <h3 className="text-base font-semibold leading-snug text-primary">
                    {selectedOrganization.name}
                  </h3>
                  <div>
                    <span
                      className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.18em] ${
                        certified ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
                      }`}
                    >
                      {certified ? (
                        <ShieldCheck size={16} aria-hidden="true" />
                      ) : (
                        <ShieldAlert size={16} aria-hidden="true" />
                      )}
                      {formatCertificationStatus(selectedOrganization.certification_status)}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-500">
                    Certification Status
                  </p>
                  <p className="text-sm leading-6 text-neutral-600">
                    {fetchError && hasSearched
                      ? 'Institution search is temporarily unavailable.'
                      : showNoResults
                        ? 'No matching institution was found in the directory.'
                        : 'Choose a school from the search results to view its certification status.'}
                  </p>
                </div>
              )}
            </aside>
          </div>
        </div>
      </div>
    </section>
  )
}
